import React from 'react'
import { useStore } from '../../../store/store'
import { Dragger } from '../../common/Dragger'
import ContainerWrapper from '../../Container/ContainerWrapper'
import { gridUnit } from '../../../constants/gridConfig'
import { isASelectionBox, simulateVariableTextBox } from '../../../utils/boxes'
import CustomTextVariable from './CustomTextVariable'
import { ItemTypes, viewerTypes } from '../../../constants/constants'

const selector = (box, propContainerId) => ({
  columns,
  landscape,
  containers,
  configuration,
  selectedContainer
}) => {
  const hasBox = !!box
  const isCustomVariableText = hasBox && box.type === ItemTypes.CUSTOM_TEXT_VARIABLE
  const drawMode = hasBox && !isCustomVariableText && columns.find((c) => c.id === box.columnId)?.drawMode
  const containerFromBox = hasBox && !isCustomVariableText && containers.find((c) => c.columnsIds.includes(box.columnId))
  const container = containerFromBox || (propContainerId ? containers.find(c => c.id === propContainerId) : null)

  // Logique pour déterminer si on affiche seulement la box ou le container entier
  // Si le container est en mode "dessin" (drawMode), on affiche toujours seulement la box, pas le container entier
  // Cela s'applique à toutes les variables (tableaux, cases à cocher, select, image, texte, etc.)
  // Exception : les selection boxes (SECTION_VARIABLE, FILE_PDF_VARIABLE, etc.) affichent toujours le container
  const hasContainer = !!container
  const isContainerInDrawMode = drawMode && hasContainer
  
  let showOnlyBox = false
  if (hasBox) {
    if (isContainerInDrawMode && !isASelectionBox(box?.type)) {
      // Container en mode dessin : afficher seulement la box
      showOnlyBox = true
    } else if (hasContainer) {
      // Container normal : afficher le container entier
      showOnlyBox = false
    } else {
      // Pas de container : utiliser la logique originale
      showOnlyBox = (drawMode && !isASelectionBox(box?.type)) || (!box?.columnId && !container)
    }
  }

  return {
    isCustomVariableText,
    container,
    drawMode,
    landscape,
    lineSpacing: configuration.lineSpacing,
    marginLeft: configuration.margins.left * gridUnit,
    marginRight: configuration.margins.right * gridUnit,
    showOnlyBox,
    hasBox,
    selectedContainer
  }
}

const FormBox = ({ boxId, containerId: propContainerId }) => {
  // Récupérer la box depuis le store
  // Le selector se réexécutera automatiquement si boxes ou variables changent
  const box = useStore(({ boxes, variables }) => {
    const boxInStore = boxes.find((b) => b.id === boxId)
    if (boxInStore) return boxInStore
    const variableEntry = Object.entries(variables).find(([key]) => key === boxId)
    if (variableEntry) return simulateVariableTextBox(variableEntry)
    return null
  })

  const isSelected = useStore(({ selectedBoxId }) => selectedBoxId === boxId)

  // Mémoriser le selector pour éviter sa recréation à chaque render
  // Utiliser boxId au lieu de box pour garantir que le selector se recrée si la box change
  // (même si la référence de l'objet box reste la même, boxId est stable)
  const memoizedSelector = React.useMemo(
    () => selector(box, propContainerId),
    // Utiliser boxId pour détecter les changements de box, et box?.type pour détecter les changements de propriétés importantes
    [boxId, box?.type, box?.columnId, propContainerId]
  )

  const { container, drawMode, marginLeft, marginRight, isCustomVariableText, showOnlyBox, hasBox, lineSpacing, selectedContainer } = useStore(memoizedSelector)

  if (!hasBox) return null

  // Logique unifiée pour les groupements complets et partiels :
  // - Si box.columnId existe : variable réelle → afficher le container (si trouvé)
  // - Si box.columnId est undefined : variable simulée → afficher le textarea
  // Pour les groupements partiels, propContainerId peut être fourni pour les variables réelles
  // mais ne doit pas être fourni pour les variables simulées (pour afficher le textarea)
  const shouldRenderContainer = container && !showOnlyBox && !isCustomVariableText;
  const shouldRenderCustomTextVariable = isCustomVariableText;

  return (
    <div style={{
      zIndex: selectedContainer && selectedContainer?.id === container?.id ? 2 : 0,
      position: 'relative',
    }}>
      {shouldRenderContainer && <ContainerWrapper
        containerId={container.id}
        fromViewer={viewerTypes.VARIABLES}
        type='container'
      />}
      {!isCustomVariableText && showOnlyBox &&
        <div style={{
          width: 'fit-content',
          paddingLeft: marginLeft,
          paddingRight: marginRight,
          position: 'relative'
        }}>
          <Dragger
            fromViewer={viewerTypes.VARIABLES}
            id={boxId}
            drawMode={drawMode}
            isSelected={isSelected}
          />
        </div>
      }
      {shouldRenderCustomTextVariable && <CustomTextVariable variableKey={boxId} />}
      <div style={{ height: lineSpacing, width: "100%" }}></div>
    </div>
  )
}

export default FormBox
